import React, { useCallback, useEffect, useRef, useState, useMemo, useContext } from "react";
import { colorCode } from "@/utils/constants/colorCode";
import { capitalizeLabel, normalizeLabel, getUniqueCount, filteredDesignerOptions, getPriceOptions, handlePricePointChangeHelper, handlePriceInputChangeHelper, getOpenFilterForHiddenFilters, getDerivedPriceMinMax, isPricePointsDisabled } from "@/helpers/plpHelper";
import { generalConstants } from "@/utils/constants/general";
import { useFilterOptions } from "@/helpers/hooks/useFilterOptions";
import FilterSection from "./FilterSection";
import { PLPFiltersTracking } from "../common/Tracking";
import { HiddenFiltersContext } from "./HiddenFiltersContext";

function FilterSections({
  facets,
  selected,
  setSelected,
  openFilter,
  setOpenFilter,
  onChange,
  clearSearchTrigger,
  hiddenFiltersState
}) {
  const {hiddenFilters} = useContext(HiddenFiltersContext); // Context to access hidden filters
  const FILTER_KEYS = generalConstants.FILTER_KEYS;
  // Label helpers
  const getCategoryLabel = useCallback(
    option => capitalizeLabel(option.display || option.label),
    []
  );
  const getColorLabel = useCallback(
    option => capitalizeLabel(option.label),
    []
  );
  const getColorHex = useCallback(
    option => colorCode.data[option.label.trim()],
    []
  );
  const getSizeLabel = useCallback(
    option => option.label,
    []
  );
  const getOccasionLabel = useCallback(
    option => capitalizeLabel(option.label),
    []
  );
  const getDesignerLabel = useCallback(
    option => option.label,
    []
  );
  const getAvailabilityLabel = useCallback(
    option => capitalizeLabel(option.label),
    []
  );
  const getSleevesLabel = useCallback(
    option => capitalizeLabel(option.label),
    []
  );
  const getMaterialLabel = useCallback(
    option => option.label,
    []
  );
  const getStoneLabel = useCallback(
    option => option.label,
    []
  );

  // Use custom hook for all filter options and titles
  const {
    lvl0Options,
    lvl1Options,
    lvl2Options,
    colorOptions,
    sizeOptions,
    occasionOptions,
    designerOptions,
    availabilityOptions,
    sleevesOptions,
    materialOptions,
    stoneOptions,
    selectedCategoryTitle,
    selectedSubcategoryTitle,
  } = useFilterOptions(facets, selected, getCategoryLabel);

  // Handle display of Size filter when selected category is 'bags and accessories'.
 const showSizeFilter = normalizeLabel(selected[FILTER_KEYS.CATEGORY]?.[0]) !== normalizeLabel("Bags & accessories");
 const handlePsykhePLPfiltersNaming = (action, facetKey, valueKey) => {
    let label = '';
    switch (facetKey) {
      case "p.f.pr.named_tags.\"categories.lvl0\"":
        label = "Cat";
        break;
      case "p.f.pr.named_tags.\"categories.lvl1\"":
        label = "Cat";
        break;
      case "p.f.pr.named_tags.\"categories.lvl2\"":
        label = "Subcat";
        break;
      case "p.f.vamf.color.base_color":
        label = "Color";
        break;
      case "p.f.vamf.custom.us_size":
        label = "Size";
        break;
      case "p.f.pr.named_tags.Occasion":
        label = "Occasion";
        break;
      case "p.f.pr.vendor":
        label = "Designer";
        break;
      case "p.f.pr.named_tags.Availability":
        label = "Availability";
        break;
      case "p.f.pr.maxPrice":
        label = "Price";
        break;
      case "p.f.pr.named_tags.Sleeves":
        label = "Sleeves";
        break;
      case "p.f.pr.named_tags.Material":
        label = "Material";
        break;
      case "p.f.pr.named_tags.Stone":
        label = "Stone";
        break;
      default:
        label = facetKey;
    }
    PLPFiltersTracking(label, valueKey, action === 'removed');
  }

  // useRef to track if openFilter state has been initialized already
  const hasOpenFilter = useRef(false);
  useEffect(() => {
    // only initialize openFilter if it hasn't been set yet and if atleast one filter is selected
    if(!hasOpenFilter.current){
      const categoryKeys = [FILTER_KEYS.CATEGORY, FILTER_KEYS.SUBCATEGORY, FILTER_KEYS.LEVEL2];
      // Check if any non-category filter is selected
      const nonCategorySelected = Object.keys(selected)
        .filter(k => !categoryKeys.includes(k) && k !== 'setByUser')
        .some(k => Array.isArray(selected[k]) && selected[k].length > 0);
      // Check if at least one sub-category is selected and level2 options exist
      const hasSubCategorySelected = Array.isArray(selected[FILTER_KEYS.SUBCATEGORY]) && selected[FILTER_KEYS.SUBCATEGORY].length > 0;
      const hasLevel2Options = Array.isArray(lvl2Options) && lvl2Options.length > 0;

      // If sub-category is selected, always close subcat (and open level2 if present)
      if (hasSubCategorySelected) {
        setOpenFilter({
          [FILTER_KEYS.CATEGORY]: false,
          [FILTER_KEYS.SUBCATEGORY]: false,
          [FILTER_KEYS.LEVEL2]: hasLevel2Options
        });
        hasOpenFilter.current = true;
        return;
      }
      if (nonCategorySelected) {
        setOpenFilter({
          [FILTER_KEYS.CATEGORY]: false,
          [FILTER_KEYS.SUBCATEGORY]: false,
          [FILTER_KEYS.LEVEL2]: false
        });
        hasOpenFilter.current = true;
        return;
      }
      // If nothing or only category filters are selected, use hidden filter logic if present
        if (
          hiddenFilters &&
          (hiddenFilters[FILTER_KEYS.CATEGORY]?.length > 0 ||
            hiddenFilters[FILTER_KEYS.SUBCATEGORY]?.length > 0 ||
            hiddenFilters[FILTER_KEYS.LEVEL2]?.length > 0)
        ) {
        // If sub-category is selected, force subcat closed and level2 open/closed
        if (hasSubCategorySelected) {
          setOpenFilter({
            [FILTER_KEYS.CATEGORY]: false,
            [FILTER_KEYS.SUBCATEGORY]: false,
            [FILTER_KEYS.LEVEL2]: hasLevel2Options
          });
        } else {
          setOpenFilter({
            [FILTER_KEYS.CATEGORY]: false,
            [FILTER_KEYS.SUBCATEGORY]: false,
            [FILTER_KEYS.LEVEL2]: false,
            ...getOpenFilterForHiddenFilters(hiddenFilters)
          });
        }
        hasOpenFilter.current = true;
        return;
      }
    }
  }, [selected, lvl1Options, lvl2Options, FILTER_KEYS, hiddenFilters]);

    const handleChange = useCallback((facetKey, valueKeys, checked) => {
     if (facetKey === FILTER_KEYS.DESIGNERS) {
       setSearchValue(""); // Clear the designer search box after selection
     }
     setSelected(prev => {
      const updated = { ...prev };
      const prevValues = updated[facetKey] || [];
      let action = '';
      if (checked) {
        // Filter was added
        action = 'added';
      } else {
        // Filter was removed
        action = 'removed';
      }
      // Always treat valueKeys as an array
      let keysArray = Array.isArray(valueKeys) ? valueKeys : [valueKeys];
      const addVariants = key => {
        if (!key || typeof key !== "string") return [key];
        if (key.includes(" ")) {
          // Capitalize each word (for the whole string)
          const capitalized = key.replace(/\b\w/g, c => c.toUpperCase());

          // Only first part before first '>' is first-word-capitalized, rest stays as is
          const parts = key.split(">");
          const firstPart = parts[0].trim();
          const firstWordCapitalized =
            firstPart.charAt(0).toUpperCase() + firstPart.slice(1).toLowerCase();
          const rest = parts.slice(1).map(p => {
            // Capitalize only the first word of each part, rest lowercase
            const trimmed = p.trim();
            return trimmed
              ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
              : "";
          });
          const camelCaseVariant =
            [firstWordCapitalized, ...rest].join(parts.length > 1 ? " > " : "");

          return [key, capitalized, camelCaseVariant];
        }
        return [key];
      };

      let variantKeys = Array.from(
        new Set(keysArray.flatMap(addVariants))
      );
  
      if (facetKey === FILTER_KEYS.CATEGORY) {
        updated[facetKey] = checked ? variantKeys : [];
        updated[FILTER_KEYS.SUBCATEGORY] = [];
        updated[FILTER_KEYS.LEVEL2] = [];
        setTimeout(() => {
        setOpenFilter(prev => ({
          ...prev,
          [facetKey]: false,
          [FILTER_KEYS.SUBCATEGORY]: true,
          [FILTER_KEYS.LEVEL2]: false,
        }));
        },0);
      } else if (facetKey === FILTER_KEYS.SUBCATEGORY) {
        updated[facetKey] = checked
          ? Array.from(new Set([...prevValues, ...variantKeys]))
          : prevValues.filter(
              v => !variantKeys.some(k => normalizeLabel(k) === normalizeLabel(v))
            );
        if (updated[facetKey].length === 0) {
          updated[FILTER_KEYS.LEVEL2] = [];
        }
        setOpenFilter(prev => ({
          ...prev,
          [facetKey]: false,
          [FILTER_KEYS.LEVEL2]: true,
        }));
      } else if (facetKey === FILTER_KEYS.LEVEL2) {
        updated[facetKey] = checked
          ? Array.from(new Set([...prevValues, ...variantKeys]))
          : prevValues.filter(
              v => !variantKeys.some(k => normalizeLabel(k) === normalizeLabel(v))
            );
        setOpenFilter(prev => ({ ...prev, [facetKey]: true }));
      } else if (
        facetKey === FILTER_KEYS.COLOR || 
        facetKey === FILTER_KEYS.SIZE || 
        facetKey === FILTER_KEYS.OCCASION ||
        facetKey === FILTER_KEYS.DESIGNERS ||
        facetKey === FILTER_KEYS.AVAILABILITY ||
        facetKey === FILTER_KEYS.PRICE ||
        facetKey === FILTER_KEYS.SLEEVES ||
        facetKey === FILTER_KEYS.MATERIAL ||
        facetKey === FILTER_KEYS.STONE
      ) {
        updated[facetKey] = checked
          ? Array.from(new Set([...prevValues, ...variantKeys]))
          : prevValues.filter(
              v => !variantKeys.some(k => normalizeLabel(k) === normalizeLabel(v))
            );
        setOpenFilter(prev => ({ ...prev, [facetKey]: true }));
      }
      // Track filter changes for analytics
      handlePsykhePLPfiltersNaming(action, facetKey, valueKeys)
      onChange && onChange({...updated, setByUser: true});
      
      return {...updated, setByUser: true};
    });
    // Scroll to top if not already at top
    if (typeof window !== "undefined" && window.scrollY > 0) {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [
    setSelected,
    setOpenFilter,
    onChange,
    FILTER_KEYS,
    lvl0Options,
    lvl1Options,
    lvl2Options,
    colorOptions,
    sizeOptions,
    occasionOptions,
    designerOptions,
    availabilityOptions,
    sleevesOptions,
    materialOptions,
    stoneOptions,
  ]);

  // Search state for filters that need it
  const [searchValue, setSearchValue] = useState("");

  // Use the helper for designer filtering
  const filteredDesigners = useMemo(
    () => filteredDesignerOptions(designerOptions, searchValue),
    [designerOptions, searchValue]
  );

  // Handler for search input
  const handleSearch = useCallback((val) => {
    setSearchValue(val);
  }, []);

  // --- Price Filter UI State ---
  const priceOptions = getPriceOptions();
  const { derivedPriceMin, derivedPriceMax } = getDerivedPriceMinMax(selected[FILTER_KEYS.PRICE], priceOptions);
  // Disable price points if custom input is active or has values
  const pricePointsDisabled = isPricePointsDisabled(derivedPriceMin, derivedPriceMax);

  // Reset designer and price search boxes when clearSearchTrigger changes
  useEffect(() => {
    setSearchValue("");
  }, [clearSearchTrigger]);

  // Replace local handlePricePointChange with imported helper
  const handlePricePointChange = (facetKey, valueKey, checked) =>
    handlePricePointChangeHelper({
      facetKey,
      valueKey,
      checked,
      priceOptions,
      setSelected,
      handlePsykhePLPfiltersNaming,
      onChange,
      generalConstants
    });

  // Replace local handlePriceInputChange with imported helper
  const handlePriceInputChange = (type, value) =>
    handlePriceInputChangeHelper({
      type,
      value,
      setSelected,
      handlePsykhePLPfiltersNaming,
      onChange,
      generalConstants,
      priceMin: derivedPriceMin,
      priceMax: derivedPriceMax
    });

  return (
    <div>
      <FilterSection
        title="Category"
        options={lvl0Options}
        selected={selected[FILTER_KEYS.CATEGORY]}
        facetKey={FILTER_KEYS.CATEGORY}
        open={openFilter[FILTER_KEYS.CATEGORY]}
        onToggle={() =>
          setOpenFilter(prev => ({
            ...prev,
            [FILTER_KEYS.CATEGORY]: !prev[FILTER_KEYS.CATEGORY]
          }))
        }
        onChange={handleChange}
        getLabel={getCategoryLabel}
        selectedCount={getUniqueCount(selected[FILTER_KEYS.CATEGORY]) || 0}
      />
      {selected[FILTER_KEYS.CATEGORY]?.length > 0 && lvl1Options?.length > 0 && (
        <FilterSection
          title={selectedCategoryTitle}
          options={lvl1Options}
          selected={selected[FILTER_KEYS.SUBCATEGORY]}
          facetKey={FILTER_KEYS.SUBCATEGORY}
          open={openFilter[FILTER_KEYS.SUBCATEGORY]}
          onToggle={() =>
            setOpenFilter(prev => ({
              ...prev,
              [FILTER_KEYS.SUBCATEGORY]: !prev[FILTER_KEYS.SUBCATEGORY]
            }))
          }
          onChange={handleChange}
          getLabel={getCategoryLabel}
          selectedCount={getUniqueCount(selected[FILTER_KEYS.SUBCATEGORY]) || 0}
        />
      )}
      {selected[FILTER_KEYS.SUBCATEGORY]?.length > 0 && lvl2Options?.length > 0 && (
        <FilterSection
          title={selectedSubcategoryTitle}
          options={lvl2Options}
          selected={selected[FILTER_KEYS.LEVEL2]}
          facetKey={FILTER_KEYS.LEVEL2}
          open={openFilter[FILTER_KEYS.LEVEL2]}
          onToggle={() =>
            setOpenFilter(prev => ({
              ...prev,
              [FILTER_KEYS.LEVEL2]: !prev[FILTER_KEYS.LEVEL2]
            }))
          }
          onChange={handleChange}
          getLabel={getCategoryLabel}
          selectedCount={getUniqueCount(selected[FILTER_KEYS.LEVEL2]) || 0}
        />
      )}
      {colorOptions.length > 0 && (
        <FilterSection
          title="Color"
          options={colorOptions}
          selected={selected[FILTER_KEYS.COLOR]}
          facetKey={FILTER_KEYS.COLOR}
          open={openFilter[FILTER_KEYS.COLOR]}
          onToggle={() =>
            setOpenFilter(prev => ({
              ...prev,
              [FILTER_KEYS.COLOR]: !prev[FILTER_KEYS.COLOR]
            }))
          }
          onChange={handleChange}
          getLabel={getColorLabel}
          getColor={getColorHex}
          styleClass="color-ul"
          selectedCount={getUniqueCount(selected[FILTER_KEYS.COLOR]) || 0}
        />
      )}
      {availabilityOptions?.length > 0 && (
        <FilterSection
          title="Availability"
          options={availabilityOptions}
          selected={selected[FILTER_KEYS.AVAILABILITY]}
          facetKey={FILTER_KEYS.AVAILABILITY}
          open={openFilter[FILTER_KEYS.AVAILABILITY]}
          onToggle={() =>
            setOpenFilter(prev => ({
              ...prev,
              [FILTER_KEYS.AVAILABILITY]: !prev[FILTER_KEYS.AVAILABILITY]
            }))
          }
          onChange={handleChange}
          getLabel={getAvailabilityLabel}
          selectedCount={getUniqueCount(selected[FILTER_KEYS.AVAILABILITY]) || 0}
          hiddenFiltersState={hiddenFiltersState}
        />
      )}
      {sizeOptions.length > 0 && showSizeFilter && (
        <FilterSection
          title="Size"
          options={sizeOptions}
          selected={selected[FILTER_KEYS.SIZE]}
          facetKey={FILTER_KEYS.SIZE}
          open={openFilter[FILTER_KEYS.SIZE]}
          onToggle={() =>
            setOpenFilter(prev => ({
              ...prev,
              [FILTER_KEYS.SIZE]: !prev[FILTER_KEYS.SIZE]
            }))
          }
          onChange={handleChange}
          getLabel={getSizeLabel}
          selectedCount={getUniqueCount(selected[FILTER_KEYS.SIZE]) || 0}
        />
      )}
      {/* --- Price Filter Section --- */}
      <FilterSection
        title="Price"
        options={priceOptions}
        selected={selected[FILTER_KEYS.PRICE]}
        facetKey={FILTER_KEYS.PRICE}
        open={openFilter[FILTER_KEYS.PRICE]}
        onToggle={() =>
          setOpenFilter(prev => ({
            ...prev,
            [FILTER_KEYS.PRICE]: !prev[FILTER_KEYS.PRICE]
          }))
        }
        onChange={handlePricePointChange}
        getLabel={opt => opt.label}
        selectedCount={Array.isArray(selected[FILTER_KEYS.PRICE]) && selected[FILTER_KEYS.PRICE].length > 0 ? 1 : 0}
        showPriceRange
        priceMin={derivedPriceMin}
        priceMax={derivedPriceMax}
        onPriceInputChange={handlePriceInputChange}
        pricePointsDisabled={pricePointsDisabled}
        onPriceInputFocus={() => {}}
        onPriceInputBlur={() => {}}
      />
      {occasionOptions?.length > 0 && (
        <FilterSection
          title="Occasion"
          options={occasionOptions}
          selected={selected[FILTER_KEYS.OCCASION]}
          facetKey={FILTER_KEYS.OCCASION}
          open={openFilter[FILTER_KEYS.OCCASION]}
          onToggle={() =>
            setOpenFilter(prev => ({
              ...prev,
              [FILTER_KEYS.OCCASION]: !prev[FILTER_KEYS.OCCASION]
            }))
          }
          onChange={handleChange}
          getLabel={getOccasionLabel}
          selectedCount={getUniqueCount(selected[FILTER_KEYS.OCCASION]) || 0}
        />
      )}
      {sleevesOptions.length > 0 && (
        <FilterSection
          title="Sleeves"
          options={sleevesOptions}
          selected={selected[FILTER_KEYS.SLEEVES]}
          facetKey={FILTER_KEYS.SLEEVES}
          open={openFilter[FILTER_KEYS.SLEEVES]}
          onToggle={() =>
            setOpenFilter(prev => ({
              ...prev,
              [FILTER_KEYS.SLEEVES]: !prev[FILTER_KEYS.SLEEVES]
            }))
          }
          onChange={handleChange}
          getLabel={getSleevesLabel}
          selectedCount={getUniqueCount(selected[FILTER_KEYS.SLEEVES]) || 0}
          hiddenFiltersState={hiddenFiltersState}
        />
      )}
      {designerOptions?.length > 0 && (
        <FilterSection
          title="Designers"
          options={filteredDesigners}
          selected={selected[FILTER_KEYS.DESIGNERS]}
          facetKey={FILTER_KEYS.DESIGNERS}
          open={openFilter[FILTER_KEYS.DESIGNERS]}
          onToggle={() =>
            setOpenFilter(prev => ({
              ...prev,
              [FILTER_KEYS.DESIGNERS]: !prev[FILTER_KEYS.DESIGNERS]
            }))
          }
          onChange={handleChange}
          getLabel={getDesignerLabel}
          selectedCount={getUniqueCount(selected[FILTER_KEYS.DESIGNERS]) || 0}
          onSearch={handleSearch}
          searchValue={searchValue}
          searchPlaceholder="Search Designers..."
        />
      )}
      {materialOptions?.length > 0 && (
        <FilterSection
          title="Material"
          options={materialOptions}
          selected={selected[FILTER_KEYS.MATERIAL]}
          facetKey={FILTER_KEYS.MATERIAL}
          open={openFilter[FILTER_KEYS.MATERIAL]}
          onToggle={() =>
            setOpenFilter(prev => ({
              ...prev,
              [FILTER_KEYS.MATERIAL]: !prev[FILTER_KEYS.MATERIAL]
            }))
          }
          onChange={handleChange}
          getLabel={getMaterialLabel}
          selectedCount={getUniqueCount(selected[FILTER_KEYS.MATERIAL]) || 0}
          hiddenFiltersState={hiddenFiltersState}
        />
      )}
      {stoneOptions?.length > 0 && (
        <FilterSection
          title="Stone"
          options={stoneOptions}
          selected={selected[FILTER_KEYS.STONE]}
          facetKey={FILTER_KEYS.STONE}
          open={openFilter[FILTER_KEYS.STONE]}
          onToggle={() =>
            setOpenFilter(prev => ({
              ...prev,
              [FILTER_KEYS.STONE]: !prev[FILTER_KEYS.STONE]
            }))
          }
          onChange={handleChange}
          getLabel={getStoneLabel}
          selectedCount={getUniqueCount(selected[FILTER_KEYS.STONE]) || 0}
          hiddenFiltersState={hiddenFiltersState}
        />
      )}
    </div>
  );
}

export default FilterSections;